import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import ERRORS from '../../constants/error';
import APPCONSTANTS from '../../constants/appConstants';
import DetailCard from '../../components/detailCard/DetailCard';
import CustomTable from '../../components/customtable/CustomTable';
import toastCenter from '../../utils/toastCenter';
import Loader from '../../components/loader/Loader';
import FilterLayout from '../../components/filter/FilterLayout';
import { assessmentListSelector, loadingSelector, totalSelector } from '../../store/report/selectors';
import { fetchAssessmentListRequest } from '../../store/report/actions';
import { downloadAssessmentListReport } from '../../services/reportAPI';

interface IMatchParams {
  siteId: string;
  siteName: string;
}

const AssessmentReport = (props: IMatchParams): React.ReactElement => {
  const fromDatePickerRef = useRef<DatePicker>(null);
  const toDatePickerRef = useRef<DatePicker>(null);
  const [listParams, setListParams] = useState({
    page: APPCONSTANTS.NUMBER.ONE,
    rowsPerPage: APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE,
    searchTerm: ''
  });

  const [fromDate, setFromDate] = useState<Date | null | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | null | undefined>(undefined);
  const [gender, setGender] = useState<Array<null | string>>([]);
  const [htnDiagnosis, setHTNDiagnosis] = useState(false);
  const [dbmDiagnosis, setDBMDiagnosis] = useState(false);
  const [provisionalDiagnosis, setProvisionalDiagnosis] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const dispatch = useDispatch();

  const assessmentList = useSelector(assessmentListSelector);
  const loading = useSelector(loadingSelector);
  const assessmentListCount = useSelector(totalSelector);

  const { siteId, siteName } = useParams<IMatchParams>();

  const prevDateFetchValue = useRef<any>({
    fromDate: null,
    toDate: null
  });

  useEffect(() => {
    if (!fromDate) {
      toDatePickerRef.current?.props.onChange(null, undefined);
      setToDate(null);
    }
    if ((fromDate && !toDate) || (fromDate && toDate)) {
      toDatePickerRef.current?.props.minDate?.setDate(fromDate.getDate());
      toDatePickerRef.current?.props.minDate?.setMonth(fromDate.getMonth());
      toDatePickerRef.current?.props.minDate?.setFullYear(fromDate.getFullYear());
    }
    if (toDate) {
      fromDatePickerRef.current?.props.maxDate?.setDate(toDate.getDate());
      fromDatePickerRef.current?.props.maxDate?.setMonth(toDate.getMonth());
      fromDatePickerRef.current?.props.maxDate?.setFullYear(toDate.getFullYear());
    }
  }, [toDatePickerRef, fromDate, toDate, loading, exportLoading]);

  const reqPayload = (newParams: any) => {
    const reqData = {
      siteId: Number(siteId),
      skip: (newParams.page - APPCONSTANTS.NUMBER.ONE) * newParams.rowsPerPage,
      limit: newParams.rowsPerPage,
      failureCb: (e: Error) => {
        if (e.message === ERRORS.NETWORK_ERROR.message) {
          toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
        } else {
          toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.ASSESSMENT_LIST_FETCH_ERROR);
        }
      }
    };
    if (fromDate && toDate) {
      Object.assign(reqData, {
        fromDate: moment(fromDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT),
        toDate: moment(toDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT)
      });
    }
    if (gender.length) {
      Object.assign(reqData, { genders: gender });
    }
    if (htnDiagnosis) {
      Object.assign(reqData, { htnDiagnosis });
    }
    if (dbmDiagnosis) {
      Object.assign(reqData, { dbmDiagnosis });
    }
    if (provisionalDiagnosis) {
      Object.assign(reqData, { provisionalDiagnosis });
    }
    return reqData;
  };

  const fetchList = (newParams: any) => {
    dispatch(
      fetchAssessmentListRequest({ payload: reqPayload(newParams) })
    );
    setListParams(newParams);
  };

  useEffect(() => {
    const newParams = {
      ...listParams,
      page: 1
    };
    fetchList(newParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, gender, htnDiagnosis, dbmDiagnosis, provisionalDiagnosis, dispatch]);

  useEffect(() => {
    const formatDate = (date: any) => date ? moment(date)
      .format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT) : null;
    const prevFromDate = formatDate(prevDateFetchValue.current?.fromDate);
    const prevToDate = formatDate(prevDateFetchValue.current?.toDate);
    const currFromDate = formatDate(fromDate);
    const currToDate = formatDate(toDate);
    const valueChanged = () => {
      return (prevFromDate !== currFromDate ||
        prevToDate !== currToDate
      );
    };
    if ((fromDate && toDate && valueChanged()) ||
      (fromDate === null && toDate === null && valueChanged())) {
      prevDateFetchValue.current = {
        fromDate: currFromDate,
        toDate: currToDate
      };
      const newParams = {
        ...listParams,
        page: 1
      };
      fetchList(newParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, dispatch]);

  const handleSearch = (searchString: string) => {
    const newParams = {
      ...listParams,
      page: APPCONSTANTS.NUMBER.ONE,
      searchTerm: searchString
    };
    fetchList(newParams);
  };

  const handlePage = (pageNo: number, rowsPerPage: number | undefined = APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE) => {
    const newParams = {
      ...listParams,
      page: pageNo,
      rowsPerPage
    };
    fetchList(newParams);
  };

  const handleGenderOnChange = (event: React.BaseSyntheticEvent) => {
    if (event.target.checked) {
      setGender(values => [...values, event.target.value]);
    } else {
      const index = gender?.indexOf(event.target.value);
      const updatedValue = gender;
      updatedValue.splice(index, 1);
      setGender(values => [...updatedValue]);
    }
  };

  const exportAssessmentList = async () => {
    try {
      setExportLoading(true);
      const payLoad = {
        siteId,
        siteName,
        limit: assessmentListCount
      };
      if (fromDate && toDate) {
        Object.assign(payLoad, {
          fromDate: moment(fromDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT),
          toDate: moment(toDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT)
        });
      }
      if (gender.length) {
        Object.assign(payLoad, { genders: gender });
      }
      if (htnDiagnosis) {
        Object.assign(payLoad, { htnDiagnosis: htnDiagnosis });
      }
      if (dbmDiagnosis) {
        Object.assign(payLoad, { dbmDiagnosis: dbmDiagnosis });
      }
      if (provisionalDiagnosis) {
        Object.assign(payLoad, { provisionalDiagnosis: provisionalDiagnosis });
      }
      await downloadAssessmentListReport(payLoad);
      setExportLoading(false);
      toastCenter.success(APPCONSTANTS.SUCCESS, APPCONSTANTS.REPORT_DOWNLOAD_SUCCESS);
    } catch {
      setExportLoading(false);
      toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.DOWNLOAD_FAIL_MSG);
    }
  };
  return (
    <>
      {loading && <Loader />}
      {exportLoading && <Loader />}
      <div className='row'>
        <FilterLayout
          fromDateSelector={(date: Date) => {
            setFromDate(date);
          }}
          toDateSelector={(date: Date) => setToDate(date)}
          handleGenderOnChange={(event: React.BaseSyntheticEvent) => handleGenderOnChange(event)}
          HTNOnChange={(event: React.BaseSyntheticEvent) => {
            setHTNDiagnosis(event.target.checked);
          }}
          DBMOnChange={(event: React.BaseSyntheticEvent) => {
            setDBMDiagnosis(event.target.checked);
          }}
          ProvisionalOnChange={(event: React.BaseSyntheticEvent) => {
            setProvisionalDiagnosis(event.target.checked);
          }}
          fromDate={fromDate}
          fromDatePickerRef={fromDatePickerRef}
          toDatePickerRef={toDatePickerRef}
          showProvisionalDiagnosis={true}
        />
        <div className='col'>
          <DetailCard
            header='Assessment Report'
            isSearch={false}
            onSearch={handleSearch}
            buttonLabel='Export'
            onButtonClick={() => exportAssessmentList()}
            isDownload={true}
            isDisabled={assessmentList.length === 0}
          >
            <CustomTable
              rowData={assessmentList}
              columnsDef={[
                {
                  id: 1,
                  name: 'firstName',
                  label: 'Name'
                },
                {
                  id: 2,
                  name: 'gender',
                  label: 'Gender'
                },
                {
                  id: 3,
                  name: 'phoneNumber',
                  label: 'Phone Number',
                  class: 'numeric'
                },
                {
                  id: 4,
                  name: 'provisional_diagnosis',
                  label: 'Provisional Diagnosis',
                  cellFormatter: (data: any) => {
                    if (data?.provisionalDiagnosis == null) {
                      return '';
                    } else {
                      return String(data.provisionalDiagnosis);
                    }
                  }
                },
                {
                  id: 5,
                  name: 'confirm_diagnosis',
                  label: 'Confirm Diagnosis',
                  width: '240px',
                  cellFormatter: (data: any) => {
                    if (data?.confirmDiagnosis == null) {
                      return '';
                    } else {
                      return String(data.confirmDiagnosis);
                    }
                  }
                },
                {
                  id: 6,
                  name: 'last_assessment_date',
                  label: 'Last BP assessment date',
                  cellFormatter: (data: any) => {
                    if (data?.lastBpAssessmentDate) {
                      return moment(data.lastBpAssessmentDate).format(APPCONSTANTS.DATE_MONTH_YEAR_FORMAT);
                    } else {
                      return '';
                    }
                  }
                },
                {
                  id: 7,
                  name: 'last_bg_assessment_date',
                  label: 'Last BG assessment date',
                  cellFormatter: (data: any) => {
                    if (data?.lastBgAssessmentDate) {
                      return moment(data.lastBgAssessmentDate).format(APPCONSTANTS.DATE_MONTH_YEAR_FORMAT);
                    } else {
                      return '';
                    }
                  }
                }
              ]}
              isDelete={false}
              isEdit={false}
              isView={false}
              isDownload={false}
              page={listParams.page}
              rowsPerPage={listParams.rowsPerPage}
              count={assessmentListCount}
              handlePageChange={handlePage}
            />
          </DetailCard>
        </div>
      </div>
    </>
  );
};

export default AssessmentReport;
